import * as React from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Checkbox, Form, Input, message } from "antd";
import { getFunctions, httpsCallable } from "firebase/functions";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleSignInButton from "../Media/GoogleSignInButton";
import { RECAPTCHA_SITE_KEY } from "../../recaptchaSiteKey";

export default function SignUp() {
  const { signup, currentUser } = useAuth();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [isFetched, setIsFetched] = React.useState(false);

  const navigate = useNavigate();

  // reCAPTCHA
  const [recaptcha, setRecaptcha] = React.useState(null);
  const recaptchaRef = React.useRef(null);
  const getFunctionsInstance = getFunctions();
  const verifyRecaptcha = httpsCallable(
    getFunctionsInstance,
    "verifyRecaptcha"
  );

  const onRecaptchaChange = (value) => setRecaptcha(value);

  React.useEffect(() => {
    if (currentUser !== undefined) setIsFetched(true);
  }, [currentUser]);

  async function onFinish(values) {
    try {
      setLoading(true);
      await verifyRecaptcha({ recaptchaToken: recaptcha });
      await signup(
        values.email,
        values.password,
        values.firstName,
        values.lastName,
        values.username,
        values.newsLetter
      );
      message.success("Account created successfully");
      navigate("/email-verification", { replace: true });
    } catch (error) {
      const msg = error?.message || "Error while creating account.";
      message.error("Failed to create account: " + msg);
      setErrorMessage(msg);
      recaptchaRef.current?.reset();
      setLoading(false);

      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
      navigate("?=account_creation_failed", { replace: true });
    }
  }

  const onFinishFailed = () => {
    setLoading(true);
    message.error("Failed to create account");
    recaptchaRef.current?.reset();
    setLoading(false);
  };

  return (
    <>
      {/* Spacer so content never sits under the fixed navbar */}
      <div aria-hidden className="h-20 sm:h-22 md:h-24" />

      {/* Page background */}
      <div className="relative min-h-[calc(100vh-6rem)] bg-slate-50">
        {/* Subtle gradient blobs */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <div className="absolute -top-24 -left-24 h-56 w-56 rounded-full bg-rose-200/50 blur-3xl" />
          <div className="absolute -bottom-20 -right-16 h-64 w-64 rounded-full bg-emerald-200/50 blur-3xl" />
        </div>

        {/* Centered card */}
        <div className="relative z-10 mx-auto flex max-w-7xl items-center justify-center px-4 sm:px-6 lg:px-8 py-10">
          <div className="w-full max-w-md">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur-sm sm:p-8">
              {/* Header */}
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-bold tracking-tight text-rose-500 sm:text-3xl">
                  Join the community
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                  Discover gastro-friendly recipes with people like you.
                </p>
              </div>

              {/* Inline error */}
              {errorMessage ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {String(errorMessage)}
                </div>
              ) : null}

              {/* Social sign-in */}
              <div className="mb-5">
                <GoogleSignInButton />
              </div>

              {/* Divider */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="h-px w-full bg-slate-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                    Or continue with email
                  </span>
                </div>
              </div>

              {/* Form */}
              <Form
                name="signup"
                layout="vertical"
                initialValues={{ remember: false, newsLetter: false }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                className="space-y-3"
              >
                {/* Name row (stacks on mobile) */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Form.Item
                    label={
                      <span className="text-sm font-medium text-slate-700">
                        First Name
                      </span>
                    }
                    name="firstName"
                    rules={[{ required: false }]}
                  >
                    <Input
                      placeholder="Jane"
                      className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span className="text-sm font-medium text-slate-700">
                        Last Name
                      </span>
                    }
                    name="lastName"
                    rules={[{ required: false }]}
                  >
                    <Input
                      placeholder="Doe"
                      className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Username
                    </span>
                  }
                  name="username"
                  rules={[
                    { required: true, message: "Please input your username!" },
                    {
                      min: 5,
                      message: "Username must be at least 5 characters long",
                    },
                    {
                      pattern: /^[a-zA-Z0-9_]+$/,
                      message: "Use letters, numbers, and underscores only",
                    },
                  ]}
                >
                  <Input
                    placeholder="gentle_foodie"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Email
                    </span>
                  }
                  name="email"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message: "Please enter a valid email",
                    },
                    { required: true, message: "Please input your Email!" },
                  ]}
                >
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Password
                    </span>
                  }
                  name="password"
                  rules={[
                    { required: true, message: "Please input your password!" },
                    {
                      min: 6,
                      message: "Password must be at least 6 characters long",
                    },
                    {
                      pattern: /(.*[!@#$%^&*()_+{}:"<>?].*)/,
                      message: "Include at least one special character",
                    },
                    {
                      pattern: /(.*[A-Z].*)/,
                      message: "Include at least one uppercase letter",
                    },
                  ]}
                >
                  <Input.Password
                    placeholder="••••••••"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                <Form.Item
                  name="confirm"
                  label={
                    <span className="text-sm font-medium text-slate-700">
                      Confirm Password
                    </span>
                  }
                  dependencies={["password"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value)
                          return Promise.resolve();
                        return Promise.reject(
                          new Error("The two passwords do not match")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="••••••••"
                    className="h-11 rounded-xl border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </Form.Item>

                {/* reCAPTCHA */}
                {RECAPTCHA_SITE_KEY ? (
                  <div className="my-2 flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={onRecaptchaChange}
                    />
                  </div>
                ) : null}

                {/* Opt-ins */}
                <div className="mt-2 space-y-2">
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox>Remember me</Checkbox>
                  </Form.Item>
                  <div>
                    <Form.Item
                      name="newsLetter"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox>
                        Opt in to our newsletter for updates & promotions
                      </Checkbox>
                    </Form.Item>
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="default"
                  htmlType="submit"
                  className="mt-2 h-11 w-full rounded-xl bg-emerald-600 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 disabled:opacity-50"
                  disabled={loading}
                  loading={loading}
                >
                  Create account
                </Button>
              </Form>

              {/* Footer link */}
              <div className="mt-6 text-center text-sm">
                <span className="text-slate-600">
                  Already have an account?{" "}
                </span>
                <a
                  href="/login"
                  className="font-semibold text-blue-600 hover:text-blue-700"
                >
                  Sign in
                </a>
              </div>
            </div>

            {/* Tiny brand footer */}
            <div className="mt-6 text-center text-xs text-slate-500">
              © {new Date().getFullYear()} Gentle-Gourmet. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
